import React from 'react';
import { Link } from 'react-router-dom';
import PageIntro from '../components/PageIntro';
import { usePageMeta, SITE_NAME } from '../lib/usePageMeta';

const tips = [
  {
    title: 'Choose precise words',
    description: 'Select words that convey your exact meaning. Avoid vague terms when specific ones are available.',
    example: "Instead of 'good', use 'excellent', 'outstanding', or 'remarkable'.",
  },
  {
    title: 'Vary your vocabulary',
    description: 'Use synonyms to avoid repetition and keep your writing engaging.',
    example: "Use 'demonstrate', 'illustrate', or 'exemplify' instead of always using 'show'.",
  },
  {
    title: 'Use active voice',
    description: 'Write in active voice to make your sentences more direct and engaging.',
    example: "Write 'The team completed the project' instead of 'The project was completed by the team'.",
  },
  {
    title: 'Eliminate redundancy',
    description: "Remove unnecessary words and phrases that don't add meaning.",
    example: "Write 'first' instead of 'first and foremost', or 'because' instead of 'due to the fact that'.",
  },
  {
    title: 'Use concrete examples',
    description: 'Support abstract concepts with specific, relatable examples.',
    example: "Instead of 'transportation', specify 'electric buses' or 'high-speed trains'.",
  },
  {
    title: 'Mind your transitions',
    description: 'Use transitional words and phrases to connect ideas smoothly.',
    example: "Use 'however', 'furthermore', 'consequently', or 'in contrast' to link sentences.",
  },
];

const WritingTipsPage = () => {
  usePageMeta(
    `Writing Tips: Choose Better Words | ${SITE_NAME}`,
    'Practical writing tips on word choice, active voice and clarity, with an example for each.'
  );

  return (
    <div className="wrap pt-12 md:pt-16">
      <PageIntro title="Writing tips">
        Small habits that make writing clearer, each with an example you can borrow.
      </PageIntro>

      <div className="mt-12 grid max-w-5xl gap-x-16 md:grid-cols-2">
        {tips.map((tip) => (
          <section key={tip.title} className="border-t border-rule py-8">
            <h2 className="font-serif text-2xl font-semibold">{tip.title}</h2>
            <p className="mt-2 leading-relaxed">{tip.description}</p>
            <p className="mt-3 font-serif text-lg italic leading-relaxed text-ink-soft">{tip.example}</p>
          </section>
        ))}
      </div>

      <p className="mt-10 max-w-2xl text-lg">
        Looking for the right word?{' '}
        <Link to="/dictionary" className="text-link">Search the dictionary</Link>.
      </p>
    </div>
  );
};

export default WritingTipsPage;
